import { type ReactNode } from 'react';
import { useIsAtLeast } from '@/hooks/useBreakpoint.ts';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet.tsx';
import { cn } from '@/lib/utils.ts';

/**
 * AppSheet — single responsive overlay primitive. Spec §9.3.
 *
 * Below `md` (768px): full-screen Sheet sliding up from the bottom,
 * sticky header + sticky footer, safe-area padding so the close button
 * isn't behind the iOS status bar and the footer isn't behind the home
 * indicator.
 *
 * At `md+`: centered Dialog with the same content. Footer is right-
 * aligned in the Dialog footer slot.
 *
 * One API, one mental model. Every modal in the redesign goes through
 * here so we don't end up with three flavors of "open in a popup".
 *
 * Usage:
 *   <AppSheet open={open} onOpenChange={setOpen} title="Add device">
 *     <AppSheet.Body>…</AppSheet.Body>
 *     <AppSheet.Footer>
 *       <Button onClick={save}>Save</Button>
 *       <Button variant="ghost" onClick={cancel}>Cancel</Button>
 *     </AppSheet.Footer>
 *   </AppSheet>
 *
 * Body + Footer are static subcomponents to keep callsites readable.
 * They don't enforce ordering — callers should put Body first, Footer
 * second — but the typings let either be omitted.
 */

export interface AppSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Override the mobile breakpoint at which to flip to Dialog. Default `md`. */
  flipAt?: 'sm' | 'md' | 'lg';
  /** Constrain the Dialog width at md+. Defaults to 'md' (Dialog default). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Make the sheet/dialog non-dismissable via outside click + ESC. */
  preventClose?: boolean;
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<AppSheetProps['size']>, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
};

function Body({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex-1 overflow-y-auto px-4 md:px-6 py-4', className)}>{children}</div>
  );
}

function Footer({ children, className }: { children: ReactNode; className?: string }) {
  // On mobile the footer is sticky-bottom with safe-area padding so it
  // isn't behind the home indicator. At md+ this same component is
  // rendered inside Dialog's footer slot which already right-aligns.
  return (
    <div
      className={cn(
        'flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 md:px-6 py-3 border-t border-border pb-[max(env(safe-area-inset-bottom),0.75rem)]',
        className,
      )}
    >
      {children}
    </div>
  );
}

function preventCloseHandlers(prevent: boolean) {
  return prevent
    ? {
        onPointerDownOutside: (e: Event) => {
          e.preventDefault();
        },
        onEscapeKeyDown: (e: Event) => {
          e.preventDefault();
        },
      }
    : {};
}

export function AppSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  flipAt = 'md',
  size = 'md',
  preventClose = false,
  className,
}: AppSheetProps) {
  // Render the dialog form at the chosen breakpoint and up. Below it,
  // render the bottom sheet form. We use a single hook + early-return
  // pattern rather than rendering both because Radix focus traps would
  // fight each other if both were mounted at once.
  const isDesktop = useIsAtLeast(flipAt);

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn('p-0 gap-0 flex flex-col max-h-[90vh]', SIZE_CLASS[size], className)}
          // Explicitly acknowledge "no description" so Radix doesn't
          // warn in dev when description prop isn't passed. The Radix
          // Description child (when present) auto-wires via context, so
          // passing undefined here doesn't break that path.
          aria-describedby={undefined}
          {...preventCloseHandlers(preventClose)}
        >
          <DialogHeader className="px-4 md:px-6 py-4 border-b border-border">
            <DialogTitle>{title}</DialogTitle>
            {description ? <DialogDescription>{description}</DialogDescription> : null}
          </DialogHeader>
          {/* Children typically include AppSheet.Body and AppSheet.Footer. */}
          {children}
          {/* DialogFooter is intentionally not auto-rendered — callers
              pass AppSheet.Footer as a child if they want one. This keeps
              the API symmetric across breakpoints. */}
          <DialogFooter className="hidden" />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className={cn(
          'h-[100dvh] sm:h-auto sm:max-h-[100dvh] flex flex-col p-0 gap-0 pt-[env(safe-area-inset-top)]',
          className,
        )}
        {...preventCloseHandlers(preventClose)}
      >
        <SheetHeader className="px-4 py-4 border-b border-border text-left">
          <SheetTitle>{title}</SheetTitle>
          {description ? <SheetDescription>{description}</SheetDescription> : null}
        </SheetHeader>
        {children}
        <SheetFooter className="hidden" />
      </SheetContent>
    </Sheet>
  );
}

AppSheet.Body = Body;
AppSheet.Footer = Footer;
